import { TableCell } from "@mui/material";
import { FC, Fragment } from "react";
import Permissions from "../../constants/permissions";
import { DownloadBundle, DownloadPart, SourceTable } from "../../graphql/types";
import { getFormattedReportDate } from "../../lib/Util";
import { DownloadAbandonButton } from "../AbandonButton";
import { HumanDate } from "../Date";
import { DownloadSamplesIcon, DownloadStatusIcon } from "../DownloadsComponents";
import { IfPermission } from "../Permissions";
import { DetailContainerRow, UploadsForDownload } from "../common/FileDetailViews";
import { NameFromHandle } from "../common/Formatters";
import IconCopy from "../common/IconCopy";
import { PurposeIcon } from "../ui/icons";
import { StatusTd, TableRowHover } from "../ui/muiTables";

interface DownloadRowProps {
  sourceTable: SourceTable | null;
  bundle: DownloadBundle;
  part?: DownloadPart;
  purpose?: string;
  showDetail?: boolean;
  allowViewUploads?: boolean;
  onClick?: (id: string) => void;
}

const canDownloadBeAbandoned = (status: string) => {
  return ["queued", "requested", "downloading", "unpacking", "retrying", "failed"].indexOf(status) > -1;
};

const DownloadRow: FC<DownloadRowProps> = ({
  sourceTable,
  bundle,
  part = null,
  purpose,
  showDetail = false,
  allowViewUploads = false,
  onClick,
}) => {
  return (
    <Fragment>
      <TableRowHover
        key={`${bundle.id}-${part ? part.id : ""}`}
        disableHover={!allowViewUploads}
        onClick={() => allowViewUploads && (onClick ? onClick(part ? part.id : bundle.id) : null)}
        className={showDetail ? "selected" : ""}
      >
        <TableCell data-testid="report-date-time">
          {getFormattedReportDate(bundle.report_datetime?.iso || null, true)}
        </TableCell>
        <TableCell>
          <HumanDate date={bundle.created} />
        </TableCell>
        <TableCell>
          <HumanDate date={bundle.started} />
        </TableCell>
        <TableCell>
          <HumanDate date={bundle.finished} />
        </TableCell>
        <TableCell>
          <div className="project">
            {sourceTable?.project} - {sourceTable?.name}
          </div>
          <span data-purpose={purpose} />
          <PurposeIcon purpose={purpose} />
          <span className="filename">
            <NameFromHandle>{bundle.handle}</NameFromHandle>
          </span>
          {part && part.legacy_flow_source_id ? (
            <Fragment>
              <IconCopy tooltip={`Download parts ID: ${part.id}`} clipboard={`Download parts ID: ${part.id}`} />
              <DownloadSamplesIcon flow_source_id={part.legacy_flow_source_id as string} finished={bundle.finished} />
            </Fragment>
          ) : (
            <IconCopy tooltip={`Download bundles ID: ${bundle.id}`} clipboard={`Download bundles ID: ${bundle.id}`} />
          )}
        </TableCell>
        <TableCell>
          <IfPermission permission={Permissions.ManagePipelines}>
            {canDownloadBeAbandoned(bundle.status) ? (
              <DownloadAbandonButton downloadStatus={bundle.status} id={bundle.id} onClick={() => {}} />
            ) : null}
          </IfPermission>
        </TableCell>
        <StatusTd>
          {part ? (
            <DownloadStatusIcon
              status={bundle.status}
              dirtyRowCount={part.dirty_row_count}
              dirtyRowStatus={part.dirty_row_status}
              newDirtyRowCount={part.new_dirty_row_count}
              newDirtyRowStatus={part.new_dirty_row_status}
              rowCount={part.row_count}
              downloadPartId={part.id}
              finished={bundle.finished}
            />
          ) : (
            <DownloadStatusIcon status={bundle.status} progress={bundle.progress} finished={bundle.finished} />
          )}
        </StatusTd>
      </TableRowHover>
      {showDetail ? (
        <DetailContainerRow>
          <TableCell colSpan={7}>
            {part ? (
              <UploadsForDownload download_part_id={part.id} />
            ) : (
              <div>Uploads will appear once downloads are completed.</div>
            )}
          </TableCell>
        </DetailContainerRow>
      ) : null}
    </Fragment>
  );
};

export { DownloadRow };
